import React, { useContext } from 'react'
import {
  Box,
  IconButton,
  Drawer,
  Typography,
  useTheme,
  ToggleButtonGroup,
  ToggleButton,
  MenuItem,
  Divider,
  styled,
} from '@mui/material'
import SettingsIcon from '@mui/icons-material/Settings'
import CloseIcon from '@mui/icons-material/Close'
import LightModeIcon from '@mui/icons-material/LightMode'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness'
import { ThemeContext } from '../theme/ThemeContext'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
// Styled icon button for consistent header icons with outline
const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.secondary.contrastText,
  transition: 'all 0.2s ease',
  padding: theme.spacing(1),
  border: `1px solid rgba(255, 255, 255, 0.2)`,
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderColor: 'rgba(255, 255, 255, 0.3)',
    transform: 'translateY(-2px)',
  },
}))

// Styled menu item for consistent settings menu
const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(0.5),
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(52, 152, 219, 0.1)',
  },
}))

const Settings = () => {
  const [open, setOpen] = React.useState(false)
  const theme = useTheme()
  const { mode, setMode } = useContext(ThemeContext)
  const navigate = useNavigate()
  const { signOut } = useAuth()
  
  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setMode(newMode)
    }
  }

  const toggleDrawer = () => {
    setOpen(!open)
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/login')
  }

  return (
    <>
      <StyledIconButton 
        onClick={toggleDrawer} 
        aria-label="settings"
        sx={{ 
          padding: { xs: 0.5, sm: 1 },
        }}
      >
        <SettingsIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
      </StyledIconButton>

      <Drawer
        anchor="right"
        open={open}
        onClose={toggleDrawer}
        PaperProps={{
          sx: {
            width: 320,
            backgroundColor: theme.palette.background.default,
            p: 3,
            borderRadius: 0,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 500 }}>Settings</Typography>
          <IconButton 
            onClick={toggleDrawer} 
            edge="end"
            sx={{
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                transform: 'rotate(90deg)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, mb: 1.5 }}>
            Theme Mode
          </Typography>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={handleModeChange}
            aria-label="theme mode"
            fullWidth
            sx={{
              '& .MuiToggleButton-root': {
                transition: 'all 0.2s ease',
                py: 1,
              }
            }}
          >
            <ToggleButton value="light" aria-label="light mode">
              <LightModeIcon sx={{ mr: 1 }} />
              Light
            </ToggleButton>
            <ToggleButton value="system" aria-label="system mode">
              <SettingsBrightnessIcon sx={{ mr: 1 }} />
              System
            </ToggleButton>
            <ToggleButton value="dark" aria-label="dark mode">
              <DarkModeIcon sx={{ mr: 1 }} />
              Dark
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, mb: 1.5 }}>
            Account
          </Typography>
          <StyledMenuItem
            component={Link}
            to="/settings"
            onClick={toggleDrawer}
          >
            Profile Settings
          </StyledMenuItem>
          <StyledMenuItem 
            onClick={() => {
              handleSignOut()
              toggleDrawer()
            }}
          >
            Sign Out
          </StyledMenuItem>
        </Box>
      </Drawer>
    </>
  )
}

export default Settings
